import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Check, ClipboardCopy, ArrowRight, ArrowLeft,
    Rocket, Store, Utensils, Beer, Pizza,
    MapPin, Grid, Order, Monitor
} from "lucide-react";
import axios from "axios";
import API from "@/utils/api";
import Logo from "@/components/ui/Logo";

const OnboardingWizard = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    // Form State (7-Minute Flow)
    const [formData, setFormData] = useState({
        restaurant_name: "",
        owner_name: "",
        owner_email: "",
        subdomain: "",
        country: "es",
        branch_address: "",
        branch_phone: "",
        menu_template: "restaurante", // restaurante, bar, pizzeria
        tables_count: "10",
        plan_key: "basic",
        product_type: "pos_delivery"
    });

    const [result, setResult] = useState(null);

    // Auto-generate subdomain
    useEffect(() => {
        if (formData.restaurant_name && !formData.subdomain) {
            const slug = formData.restaurant_name
                .toLowerCase()
                .replace(/[^a-z0-9]/g, '-')
                .replace(/-+/g, '-')
                .slice(0, 20);
            setFormData(prev => ({ ...prev, subdomain: slug }));
        }
    }, [formData.restaurant_name]);

    const handleNext = () => setStep(s => s + 1);
    const handlePrev = () => setStep(s => s - 1);

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${API}/onboarding/start`, {
                ...formData,
                restaurant_name: formData.restaurant_name,
                owner_email: formData.owner_email,
                owner_name: formData.owner_name,
                subdomain: formData.subdomain,
                menu_template: formData.menu_template,
                // Módulos extra por defecto según el flow 7m
                modules: {
                    qr_module: true,
                    delivery_integrations: true,
                    multi_printer: false
                }
            });
            setResult(response.data);
            setStep(6); // Success
            toast.success("¡Tu restaurante está listo!");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Error al crear el restaurante");
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copiado");
    };

    return (
        <div style={s.root} className="animate-in fade-in duration-700">
            {/* Header */}
            <div className="w-full max-w-4xl mb-10 flex flex-col items-center text-center">
                <Logo size={40} className="mb-6" />
                <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
                    Impulsa tu negocio en <span className="text-amber-500">7 minutos</span>
                </h1>
                <p className="text-slate-400 text-lg max-w-2xl">
                    Configuramos tu TPV, carta interactiva y sistema de gestión en un abrir y cerrar de ojos.
                </p>
            </div>

            {/* Stepper Indicator */}
            <div className="flex items-center gap-2 mb-8">
                {[1, 2, 3, 4, 5].map((i) => (
                    <div
                        key={i}
                        className={`h-1.5 w-12 rounded-full transition-all duration-500 ${step >= i ? 'bg-amber-500' : 'bg-white/10'}`}
                    />
                ))}
            </div>

            <Card className="w-full max-w-3xl bg-black/40 border border-white/5 backdrop-blur-3xl shadow-2xl overflow-hidden min-h-[500px] flex flex-col">

                {/* Paso 1: Datos de Identidad */}
                {step === 1 && (
                    <div className="p-8 md:p-12 flex-1 flex flex-col">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                                <Rocket className="text-amber-500" /> Identidad de marca
                            </h2>
                            <p className="text-slate-400">¿Cómo se llamará tu templo gastronómico?</p>
                        </div>
                        <div className="space-y-6 flex-1">
                            <div className="space-y-2">
                                <Label className="text-white text-xs uppercase font-black opacity-50">Nombre del Restaurante</Label>
                                <Input
                                    style={s.input}
                                    placeholder="Ej: El Rincón de la Pasta"
                                    value={formData.restaurant_name}
                                    onChange={e => setFormData({ ...formData, restaurant_name: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label className="text-white text-xs uppercase font-black opacity-50">Email de acceso</Label>
                                    <Input
                                        style={s.input}
                                        type="email"
                                        placeholder="paco@gmail.com"
                                        value={formData.owner_email}
                                        onChange={e => setFormData({ ...formData, owner_email: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white text-xs uppercase font-black opacity-50">Tu alias / Nombre</Label>
                                    <Input
                                        style={s.input}
                                        placeholder="Paco García"
                                        value={formData.owner_name}
                                        onChange={e => setFormData({ ...formData, owner_name: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white text-xs uppercase font-black opacity-50">URL del restaurante</Label>
                                <div className="flex items-center">
                                    <span className="bg-white/5 border border-r-0 border-white/10 text-slate-500 px-4 py-3 rounded-l-xl text-sm">https://</span>
                                    <Input
                                        style={{ ...s.input, borderRadius: '0 0.75rem 0.75rem 0' }}
                                        placeholder="nombre-restaurante"
                                        value={formData.subdomain}
                                        onChange={e => setFormData({ ...formData, subdomain: e.target.value })}
                                    />
                                    <span className="bg-white/5 border border-l-0 border-white/10 text-slate-500 px-4 py-3 rounded-r-xl text-sm">.conexopos.com</span>
                                </div>
                            </div>
                        </div>
                        <div className="mt-10 flex justify-end">
                            <button
                                className="btn-premium py-3 px-8 text-lg font-black tracking-tight"
                                onClick={handleNext}
                                disabled={!formData.restaurant_name || !formData.owner_email || !formData.subdomain}
                            >
                                Continuar <ArrowRight className="ml-2 w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Paso 2: La Sucursal */}
                {step === 2 && (
                    <div className="p-8 md:p-12 flex-1 flex flex-col">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                                <MapPin className="text-amber-500" /> ¿Dónde estamos?
                            </h2>
                            <p className="text-slate-400">Configuraremos tu oficina principal.</p>
                        </div>
                        <div className="space-y-6 flex-1">
                            <div className="space-y-2">
                                <Label className="text-white text-xs uppercase font-black opacity-50">Dirección Física</Label>
                                <Input
                                    style={s.input}
                                    placeholder="Calle Mayor 45, Planta Baja"
                                    value={formData.branch_address}
                                    onChange={e => setFormData({ ...formData, branch_address: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white text-xs uppercase font-black opacity-50">Teléfono de contacto</Label>
                                <Input
                                    style={s.input}
                                    placeholder="+34 600 000 000"
                                    value={formData.branch_phone}
                                    onChange={e => setFormData({ ...formData, branch_phone: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="mt-10 flex justify-between">
                            <Button variant="ghost" onClick={handlePrev} className="text-slate-400">Volver</Button>
                            <button className="btn-premium py-3 px-8 font-black" onClick={handleNext}>Continuar</button>
                        </div>
                    </div>
                )}

                {/* Paso 3: Menú templates */}
                {step === 3 && (
                    <div className="p-8 md:p-12 flex-1 flex flex-col">
                        <div className="mb-8 text-center md:text-left">
                            <h2 className="text-2xl font-bold text-white mb-2 flex items-center justify-center md:justify-start gap-3">
                                <Utensils className="text-amber-500" /> Identidad Gastronómica
                            </h2>
                            <p className="text-slate-400">Elige una plantilla base para auto-generar tu menú y branding.</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                            {[
                                { id: 'restaurante', icon: <Utensils />, label: 'Restaurante', desc: 'Sleek & Bistro', color: '#6366f1' },
                                { id: 'bar', icon: <Beer />, label: 'Bar & Pub', desc: 'Craft & Draft', color: '#fbbf24' },
                                { id: 'pizzeria', icon: <Pizza />, label: 'Pizzería', desc: 'Traditional Red', color: '#ef4444' },
                                { id: 'taco_bar', icon: <Rocket />, label: 'Tacos / Mex', desc: 'Vibrant & Spicy', color: '#10b981' }
                            ].map(m => (
                                <div
                                    key={m.id}
                                    onClick={() => setFormData({ ...formData, menu_template: m.id })}
                                    className={`relative p-5 rounded-3xl border-2 transition-all duration-500 cursor-pointer flex flex-col items-center text-center gap-3 group ${formData.menu_template === m.id ? 'border-none ring-2 ring-offset-4 ring-offset-black ring-amber-500 bg-amber-500 text-black' : 'border-white/5 bg-white/5 opacity-60 hover:opacity-100'}`}
                                >
                                    {formData.menu_template === m.id && (
                                        <div className="absolute top-2 right-2 bg-black rounded-full p-1 border border-amber-400">
                                            <Check className="w-3 h-3 text-amber-400" />
                                        </div>
                                    )}
                                    <div className={`p-4 rounded-2xl transition-transform duration-500 group-hover:scale-110 ${formData.menu_template === m.id ? 'bg-black/20 text-black' : 'bg-black/40 text-white'}`} style={{ color: formData.menu_template === m.id ? 'black' : m.color }}>
                                        {React.cloneElement(m.icon, { size: 36, strokeWidth: 2.5 })}
                                    </div>
                                    <div>
                                        <h3 className="font-black text-[12px] uppercase tracking-tighter">{m.label}</h3>
                                        <p className={`text-[9px] font-bold uppercase tracking-widest opacity-60 ${formData.menu_template === m.id ? 'text-black' : 'text-slate-500'}`}>{m.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-10 flex justify-between">
                            <Button variant="ghost" onClick={handlePrev} className="text-slate-400 hover:text-white transition-colors">
                                <ArrowLeft className="mr-2 w-4 h-4" /> Volver
                            </Button>
                            <button className="btn-premium py-3 px-10 font-black tracking-tight flex items-center gap-2 group" onClick={handleNext}>
                                Siguiente Paso <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Paso 4: Mesas */}
                {step === 4 && (
                    <div className="p-8 md:p-12 flex-1 flex flex-col">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                                <Grid className="text-amber-500" /> Mapa de Mesas y QR
                            </h2>
                            <p className="text-slate-400">Generaremos el layout inicial para que empieces a vender.</p>
                        </div>
                        <div className="space-y-8 flex-1">
                            <div className="bg-amber-500/5 border border-amber-500/20 p-6 rounded-2xl">
                                <Label className="text-amber-500 text-xs uppercase font-black block mb-4 text-center">Desliza para elegir número de mesas</Label>
                                <div className="flex items-center gap-8">
                                    <input
                                        type="range" min="1" max="50" step="1"
                                        value={formData.tables_count}
                                        onChange={e => setFormData({ ...formData, tables_count: e.target.value })}
                                        className="flex-1 accent-amber-500"
                                    />
                                    <span className="text-4xl font-black text-white w-20 text-right">{formData.tables_count}</span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-white/5 p-4 rounded-xl flex items-center gap-3">
                                    <Check className="text-green-500" size={16} />
                                    <span className="text-xs text-white">QR por mesa incluido</span>
                                </div>
                                <div className="bg-white/5 p-4 rounded-xl flex items-center gap-3">
                                    <Check className="text-green-500" size={16} />
                                    <span className="text-xs text-white">Layout auto-generado</span>
                                </div>
                            </div>
                        </div>
                        <div className="mt-10 flex justify-between">
                            <Button variant="ghost" onClick={handlePrev} className="text-slate-400">Volver</Button>
                            <button className="btn-premium py-3 px-8 font-black" onClick={handleNext}>Continuar</button>
                        </div>
                    </div>
                )}

                {/* Paso 5: Resumen */}
                {step === 5 && (
                    <div className="p-8 md:p-12 flex-1 flex flex-col">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                                <Rocket className="text-amber-500" /> Prueba de lanzamiento
                            </h2>
                            <p className="text-slate-400">Todo parece correcto. Recibirás tu panel básico activo.</p>
                        </div>
                        <div className="bg-white/5 rounded-3xl p-8 space-y-4 border border-white/5 flex-1 overflow-y-auto">
                            <div className="flex justify-between items-center py-3 border-b border-white/5">
                                <span className="text-slate-500 text-sm">Empresa</span>
                                <span className="text-white font-bold">{formData.restaurant_name}</span>
                            </div>
                            <div className="flex justify-between items-center py-3 border-b border-white/5">
                                <span className="text-slate-500 text-sm">Admin</span>
                                <span className="text-white font-medium">{formData.owner_email}</span>
                            </div>
                            <div className="flex justify-between items-center py-3 border-b border-white/5">
                                <span className="text-slate-500 text-sm">Menú Inicial</span>
                                <span className="text-amber-500 font-bold uppercase text-xs">{formData.menu_template}</span>
                            </div>
                            <div className="flex justify-between items-center py-3">
                                <span className="text-slate-500 text-sm">Plan</span>
                                <span className="text-white font-black">7 DÍAS GRATIS (Trial Basic)</span>
                            </div>
                        </div>
                        <div className="mt-10 flex justify-between">
                            <Button variant="ghost" onClick={handlePrev} className="text-slate-400" disabled={loading}>Volver</Button>
                            <button
                                className="btn-premium py-4 px-12 text-lg font-black shadow-amber-500/20 shadow-xl"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? "Iniciando sistema..." : "Lanzar mi Restaurante →"}
                            </button>
                        </div>
                    </div>
                )}

                {/* Paso 6: Éxito y Credenciales */}
                {step === 6 && result && (
                    <div className="flex flex-col flex-1 animate-in zoom-in duration-300">
                        <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-8 md:p-12 text-center">
                            <div className="mx-auto w-20 h-20 bg-black/10 rounded-3xl flex items-center justify-center mb-6">
                                <Check className="text-white" size={40} />
                            </div>
                            <h2 className="text-3xl font-black text-black">¡Empresa creada con éxito!</h2>
                            <p className="text-black/70 font-medium">Ya puedes empezar a vender con Conexo POS.</p>
                        </div>
                        <div className="p-8 md:p-12 flex-1 space-y-8">
                            <div className="bg-black/40 border border-white/10 rounded-3xl p-8 relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 blur-3xl rounded-full" />
                                <h4 className="flex items-center gap-2 text-amber-500 font-black mb-4 uppercase tracking-widest text-xs">
                                    Credenciales de acceso
                                </h4>
                                <div className="space-y-6 relative z-10">
                                    <div className="space-y-1">
                                        <Label className="text-[10px] text-slate-500 font-black uppercase">DNI Digital del Administrador</Label>
                                        <div className="text-xl font-bold text-white mb-2">{result.admin_email}</div>
                                        <div className="flex items-center justify-between gap-4 p-4 bg-amber-500/10 rounded-2xl border border-amber-500/30">
                                            <span className="text-2xl font-black text-amber-500 font-mono">{result.admin_password_plaintext}</span>
                                            <Button variant="ghost" size="icon" onClick={() => copyToClipboard(result.admin_password_plaintext)} className="text-amber-500 hover:bg-amber-500/10">
                                                <ClipboardCopy size={20} />
                                            </Button>
                                        </div>
                                    </div>

                                    {result.staff_users && result.staff_users.length > 0 && (
                                        <div className="pt-6 border-t border-white/5 space-y-4">
                                            <Label className="text-[10px] text-slate-500 font-black uppercase">Personal de Servicio Autogenerado</Label>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {result.staff_users.map((staff, idx) => (
                                                    <div key={idx} className="bg-white/5 p-3 rounded-xl border border-white/5 flex flex-col gap-1">
                                                        <div className="flex justify-between items-center text-[10px] font-black uppercase text-amber-500/70">
                                                            <span>{staff.role === 'cashier' ? 'Cajero' : staff.role === 'kitchen' ? 'Cocina' : staff.role === 'screen' ? 'Pantalla' : 'Repartidor'}</span>
                                                            <button onClick={() => copyToClipboard(staff.password)} className="hover:text-white transition-colors"><ClipboardCopy size={12} /></button>
                                                        </div>
                                                        <div className="text-xs text-white truncate font-bold">{staff.email}</div>
                                                        <div className="text-xs text-amber-500 font-mono font-black">{staff.password}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <p className="text-[10px] text-red-500/80 mt-4 font-bold rounded-lg bg-red-500/5 p-3 border border-red-500/10 uppercase tracking-tighter">
                                        ⚠️ Importante: Estas credenciales solo se muestran una vez. Guárdalas en un gestor de contraseñas.
                                    </p>
                                </div>
                            </div>

                            <button
                                onClick={() => navigate("/staff")}
                                className="w-full btn-premium py-5 text-xl font-black shadow-2xl"
                            >
                                Entrar al Panel de Control →
                            </button>
                        </div>
                    </div>
                )}

            </Card>

            <p className="mt-8 text-slate-500 text-xs text-center max-w-lg">
                Al crear tu cuenta aceptas nuestros Términos de Servicio y Política de Privacidad.
                Recibirás un email de bienvenida con los siguientes pasos para configurar tu impresora y TPV.
            </p>
        </div>
    );
};

const s = {
    root: {
        minHeight: "100vh", background: "#020617",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "4rem 1.5rem", fontFamily: "'Inter', sans-serif"
    },
    input: {
        background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "0.75rem", padding: "1rem", color: "#fff", fontSize: "1rem",
        outline: "none", width: "100%", transition: "all 0.2s"
    }
};

export default OnboardingWizard;
